import React from "react";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormWatch,
} from "react-hook-form";
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { formatAadharDisplay, maskAadhar, normalizeAadhar } from "../../utils/aadhar";

export type CardHolderFormValues = {
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  aadharNumber: string;
  city: string;
  block: string;
  district: string;
  state: string;
  address: string;
  status: "active" | "inactive";
};

type Props = {
  control: Control<CardHolderFormValues>;
  errors: FieldErrors<CardHolderFormValues>;
  watch: UseFormWatch<CardHolderFormValues>;
  showStatus?: boolean;
};

export const emptyCardHolderFormValues: CardHolderFormValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  aadharNumber: "",
  city: "",
  block: "",
  district: "",
  state: "",
  address: "",
  status: "active",
};

const CardHolderFormFields: React.FC<Props> = ({
  control,
  errors,
  watch,
  showStatus = false,
}) => {
  const aadharWatch = watch("aadharNumber");
  const masked =
    normalizeAadhar(aadharWatch).length === 12 ? maskAadhar(aadharWatch) : null;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="First name"
              required
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Last name"
              required
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Phone"
              required
              error={!!errors.phone}
              helperText={errors.phone?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Email (optional)"
              type="email"
              error={!!errors.email}
              helperText={errors.email?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          name="aadharNumber"
          control={control}
          render={({ field }) => (
            <TextField
              fullWidth
              label="Aadhar number"
              required
              value={formatAadharDisplay(field.value)}
              onChange={(e) => field.onChange(normalizeAadhar(e.target.value))}
              onBlur={field.onBlur}
              inputProps={{ inputMode: "numeric", maxLength: 14 }}
              error={!!errors.aadharNumber}
              helperText={
                errors.aadharNumber?.message ||
                (masked ? `Display: ${masked}` : "12-digit Aadhar (required)")
              }
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: -0.5, display: "block" }}>
          Address
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          name="city"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="City"
              required
              error={!!errors.city}
              helperText={errors.city?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          name="block"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Block"
              required
              error={!!errors.block}
              helperText={errors.block?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          name="district"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="District"
              required
              error={!!errors.district}
              helperText={errors.district?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          name="state"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="State"
              required
              error={!!errors.state}
              helperText={errors.state?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name="address"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Complete address"
              required
              multiline
              rows={2}
              error={!!errors.address}
              helperText={errors.address?.message}
            />
          )}
        />
      </Grid>
      {showStatus && (
        <Grid item xs={12} sm={6}>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.status}>
                <InputLabel>Status</InputLabel>
                <Select {...field} label="Status">
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
                <FormHelperText>{errors.status?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default CardHolderFormFields;
